import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  TIER_NAMES,
  dismissTrialPrompt,
  startPlanCheckout,
  type Me,
  type OnboardingView,
  type PlanInterval,
  type TrialOffer,
} from "../../lib/api";
import { API_URL } from "../../lib/config";
import { openCheckout } from "../../lib/checkout";
import { savePendingPlan } from "../../lib/draft";
import { doorQuery, onboardingQuery, planQuery, qk } from "../../lib/queries";
import { track } from "../../lib/telemetry";
import { WrongAccount } from "../ui/account";
import { TRIAL_CONSENT, trialLimitLine, trialPriceLine } from "../plan/plan";
import { checkoutMessageFor } from "../plan/plan-area";
import { OnboardFace, OnboardSheet, OnboardShell, StepDots } from "./steps";

/**
 * STEP 3 OF THE RUN, THE PLAN (PRD §2.2.4, FR-5.8, FR-5.9; rulings C5,
 * R-0826-3, R-0916-1).
 *
 * WHY THE MONEY STEP IS IN THE MIDDLE, since it is the question everybody asks
 * of this screen: Nob cannot draft House Rules, read an X account or screen
 * anything without doing AI work, and AI work needs a plan (§2.2.1). Putting
 * the plan choice after the payoff would mean doing work we cannot bill for or
 * showing a paywall in the middle of the reward; putting it first, before
 * anybody has seen anything, is the decision tax v3.2 rightly refused. Here it
 * lands after the identity is confirmed and before the first thing that costs
 * us money — and it is barely a choice, because the trial is SELECTED
 * (FR-5.8 #1) rather than picked off a menu.
 *
 * WHEN THE PERSON ARRIVED TO KNOCK, THE SCREEN NAMES THE KNOCK (§2.3.2 #3).
 * That line goes ABOVE the plan talk, not under it: the waiting knock is the
 * reason a plan is suddenly being asked for, and generic plan copy here wastes
 * the strongest moment in the funnel.
 *
 * NO CAPACITY IS STATED, HERE LEAST OF ALL (ruling C2). This is checkout, which
 * is exactly where the tempting refactor puts the number — "a buyer must see
 * what they are committing to". What a buyer sees is the PRICE, the interval,
 * the date of the first charge and the fair-use sentence the server authored.
 * The trial's limit is described as sized to the trial's length and never
 * quantified (FR-5.8 #3, R-0905-8).
 */

export const PERK_CHECK_TITLE = "Checking X Premium.";
/**
 * Adapted from the reference build, which read "Premium accounts get credits on
 * the house". Ruling C3 deleted the Founder's Grant and R-0916-1 deleted the
 * credits themselves, so there is nothing on the house to promise; what a
 * Premium account actually gets is the longer trial of the higher tier
 * (FR-5.8), and that is what this says.
 */
export const PERK_CHECK_LEAD =
  "Premium accounts get the longer trial, so your Nob can actually work.";
export const PERK_PREMIUM_TITLE = "X Premium on this account.";
export const PERK_REGULAR_TITLE = "No X Premium on this one.";
export const PERK_LEAD =
  "Having a Nob is free. A plan is what pays for AI work — talking to your Nob, and knocking at other people’s.";

/** The held knock's own sentence (§2.3.2 #3), naming the door by first name. */
export function heldLead(firstName: string): string {
  return (
    `${firstName}’s Nob reads it, checks what you sent and decides. ` +
    `That work takes a plan. Having a Nob of your own is free.`
  );
}

export function heldWaiting(firstName: string): string {
  return `Your knock to ${firstName} is written and waiting.`;
}

export function trialSentence(offer: TrialOffer): string {
  const account = offer.premium ? "X Premium" : "regular X account";
  return `Your ${account} gives you a ${offer.days} day free trial of the ${TIER_NAMES[offer.tier]} plan.`;
}

export function startTrialLabel(offer: TrialOffer): string {
  return `Start ${TIER_NAMES[offer.tier]} ${offer.days} day trial`;
}

export const SKIP_LABEL = "Skip for now";
/** With no trial to refuse there is nothing being given up, so nothing to confirm. */
export const CONTINUE_FREE_LABEL = "Continue on Free";
/** Skipping costs nothing, so it asks nothing: this sits under the control and says why (ruling R-1006-2). */
export const SKIP_NOTE = "You can start your trial any time from your plan.";
export const PERK_UNREACHABLE = "Nob is not answering. Try that again.";
/** The step has nothing to sell and nothing to promise; it says so and moves on. */
export const NO_TRIAL_LEAD =
  "There is no trial to start on this account. The plans are in the console whenever you want one.";

/**
 * THE GAP BETWEEN THE CARD AND THE PLAN (owner, staging walk 2026-09-18).
 *
 * The card is taken in a modal and the modal closes the instant the processor
 * says so — and at that instant nothing has been bought as far as this product
 * is concerned. `perk_pending` is computed from the trial while the tier is
 * still `free` (`routes/onboardingFlow.ts`), and the ONLY writer of the tier is
 * the webhook (`billing/subscriptions.ts`), which arrives seconds later. So the
 * single re-read fired at the paid outcome read a run that still owed this
 * step, the route drew step 3 again, and the sheet the buyer had just paid
 * through was back on screen over a purchase that was going through. A refresh
 * cleared it, which is the shape of a race rather than of a failure.
 *
 * What the screen does instead is WAIT WHERE IT IS AND SAY SO: no sheet, no
 * control that could start a second checkout, and the run re-read on a bounded
 * schedule until the flag moves on its own. The step stays DERIVED from the
 * flags — nothing here sets a step number — so the route walks on by itself the
 * moment the webhook has landed.
 */
export const PERK_FINISHING = "Card taken. Nob is writing the plan down.";
/**
 * And when the webhook is slower than the window: nothing failed, nothing is
 * lost, so this is a statement rather than an error. The tier binds when the
 * webhook lands and a later render moves the run on — so the poster comes back
 * with it, rather than holding somebody on a screen with no controls for as
 * long as a processor feels like taking.
 */
export const PERK_FINISHING_SLOW =
  "Nob is still writing it down. This page will catch up on its own.";

type FinishTiming = { poll: number; window: number };
/** The defaults, kept apart so a test can put the pair back by machinery. */
export const PERK_FINISH_DEFAULTS: Readonly<FinishTiming> = Object.freeze({
  poll: 1_500,
  window: 20_000,
});
/**
 * How that wait is timed: re-read every `poll` ms, for at most `window`.
 *
 * Mutable on purpose. The window is twenty seconds — long enough for a webhook
 * on a bad day, far too long for a test to sit through — and the alternative to
 * a knob here is fake timers over a React render loop and a query client's own
 * scheduler. The interaction test shrinks both and puts them back in the hook
 * every one of its tests runs through; nothing in the app writes to it.
 */
export const PERK_FINISH_TIMING: FinishTiming = { ...PERK_FINISH_DEFAULTS };

type Props = {
  me: Me;
  /** The door a held knock is addressed to, bare, or null for an owner's run. */
  heldHandle: string | null;
  /** Answered: the run moves on. The caller decides where to. */
  onAnswered: () => void;
};

export function OnboardingPerk({ me, heldHandle, onAnswered }: Props) {
  const queryClient = useQueryClient();
  const { data: view, isPending } = useQuery(planQuery());
  /**
   * Only for the one line that names the knock. A door that will not load is
   * not a reason to stall the money step, so the handle stands in for the name.
   */
  const door = useQuery({ ...doorQuery(heldHandle ?? ""), enabled: heldHandle !== null });

  const [interval, setInterval] = useState<PlanInterval>("monthly");
  const [accepted, setAccepted] = useState(false);
  /**
   * The UK/EEA withdrawal-right acknowledgement (ToU 2.1 §8.6, ruling R-0914-1
   * (3)) — the second control the plans view has always carried and this sheet
   * did not (wave 103). A UK account's trial was refused every time for want of
   * it, and the refusal read as "Nob is not answering".
   */
  const [waived, setWaived] = useState(false);
  const [pay, setPay] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** `writing` while the run is being watched; `slow` once the window is out. */
  const [finishing, setFinishing] = useState<"writing" | "slow" | null>(null);

  /**
   * The run, re-read while the plan is being written down — and not otherwise.
   *
   * A SECOND OBSERVER of the query the route already holds, which is what makes
   * this a poll rather than a hand-rolled interval: the route keeps reading the
   * same cache entry, TanStack schedules the refetch, and the observer asking
   * for it going away is what stops it. The cache is what carries the answer
   * back to the route, so nothing has to be handed upwards.
   */
  useQuery({
    ...onboardingQuery(),
    refetchInterval: finishing === "writing" ? PERK_FINISH_TIMING.poll : false,
  });

  /**
   * AND WHEN IT MOVES, THE TWO FACTS THE TIER IS READ FROM MOVE WITH IT (owner,
   * staging walk 2026-09-18).
   *
   * `perk_pending` is DERIVED from the tier, so the render in which it goes
   * false is the render in which the subscription is bound — and the plan, read
   * by the sidebar's tier line and by the usage ring, was last invalidated at
   * checkout close, before the webhook. Left alone it sits there for the 30 s
   * stale window: the console said Free over a Lite plan that had been paid for,
   * until somebody reloaded. The doorman thread goes with it because the meter
   * under that composer is the same fact.
   *
   * WHY THIS IS A CLEANUP AND NOT AN EFFECT ON THE OBSERVER'S DATA. The route
   * observes the SAME cache entry, so the update that flips the flag re-renders
   * it in the same batch — and its next render does not include this component
   * at all. An effect on the observed data would be scheduled on a render that
   * never happens. The wait ENDING is the event, and it is the one thing an
   * unmounted component still gets told about; the flag is read from the cache
   * here so that the window running out (writing → slow) is not mistaken for it.
   */
  useEffect(() => {
    if (finishing !== "writing") return;
    return () => {
      const view = queryClient.getQueryData<OnboardingView>(qk.onboarding());
      if (view?.onboarding.perk_pending !== false) return;
      void queryClient.invalidateQueries({ queryKey: qk.plan() });
      void queryClient.invalidateQueries({ queryKey: qk.doormanThread() });
    };
  }, [finishing, queryClient]);

  /** The far end of the window. Nothing failed when it passes; the wait ends. */
  useEffect(() => {
    if (finishing !== "writing") return;
    const timer = setTimeout(() => setFinishing("slow"), PERK_FINISH_TIMING.window);
    return () => clearTimeout(timer);
  }, [finishing]);

  const skip = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const r = await dismissTrialPrompt();
    setBusy(false);
    if (!r.ok) {
      setError(PERK_UNREACHABLE);
      return;
    }
    track({ name: "onboarding_trial_skipped" });
    await queryClient.invalidateQueries({ queryKey: qk.onboarding() });
    await queryClient.invalidateQueries({ queryKey: qk.plan() });
    onAnswered();
  }, [busy, onAnswered, queryClient]);

  /** The check is the read, not a timer: the answer arrives when it arrives. */
  if (isPending) {
    return (
      <OnboardShell>
        <StepDots step={3} knocker={heldHandle !== null} />
        <OnboardFace mood="watching" />
        <h1 className="nob-poster-title">{PERK_CHECK_TITLE}</h1>
        <p className="nob-breathe nob-poster-lead">{PERK_CHECK_LEAD}</p>
      </OnboardShell>
    );
  }

  const trial = view?.trial ?? null;
  const option = trial
    ? (trial.options.find((o) => o.interval === interval) ?? trial.options[0] ?? null)
    : null;
  const firstName = (door.data?.display_name || heldHandle || "").split(" ")[0] ?? "";
  /**
   * THE SERVER SAYS WHETHER THE §8.6 CONTROL APPLIES, and hands down its exact
   * words — the same field, and the same gating, as the plans view. This side
   * never works out a jurisdiction for itself: a client that could decide the
   * control applied could decide it away.
   */
  const waiver = view?.withdrawal_waiver ?? null;

  /** A sheet that closes takes its sentence with it; nothing is left behind it. */
  function closeSheet(): void {
    setPay(false);
    setError(null);
  }

  async function startTrial(): Promise<void> {
    const drawdown = view?.drawdown ?? null;
    if (!trial || !option || busy || drawdown === null) return;
    setBusy(true);
    setError(null);
    const r = await startPlanCheckout({
      tier: trial.tier,
      // The server's drawdown sentence, rendered beside the disclosure and
      // hashed as rendered (Terms 2.11 §5.3, §8.7).
      drawdown,
      interval: option.interval,
      first_charge_on: option.first_charge_on,
      disclosure: option.disclosure,
      // Still "/", and it still matters — but only for the one case that has a
      // browser left to send: FakeStripe reached at the top level rather than
      // in the modal. The modal itself never leaves the wizard, which is the
      // stronger version of what this field was added to fix (ruling R-0825-2).
      return_to: "/",
      // Hashed on the way out exactly as rendered; the server refuses a UK/EEA
      // checkout without it (ToU 2.1 §8.6).
      withdrawal_waiver: waiver,
      surface: "onboarding",
    });
    if (!r.ok) {
      setBusy(false);
      // A REFUSAL SAYS WHAT IT IS (wave 103). Every `!r.ok` used to be "Nob is
      // not answering", including the deliberate 409 a UK account got on every
      // attempt; the catch-all is now the dead connection and the 5xx alone.
      setError(checkoutMessageFor(r.code));
      if (r.code === "CONSENT_MISMATCH") {
        // The sentence says the terms changed, so the sheet gets them: the
        // plan is re-read — the §8.6 control appears if it now applies — and
        // both ticks are taken back, because a tick given to the old words is
        // not a consent to the new ones.
        setAccepted(false);
        setWaived(false);
        void queryClient.invalidateQueries({ queryKey: qk.plan() });
      }
      return;
    }
    // Remembered only so the plan can be NAMED once it lands; the webhook, not
    // this, is what says it happened.
    savePendingPlan(trial.tier);
    track({ name: "onboarding_trial_started", tier: trial.tier });
    setBusy(false);
    openCheckout({
      opening: r.opening,
      onDone: (result) => {
        if (result !== "paid") {
          setError(null);
          return;
        }
        // The sheet is done with — it must never be what a buyer is looking at
        // while their purchase settles — and the wait begins. This invalidation
        // is the FIRST re-read; the rest are the interval above. The step is
        // not answered here and `onAnswered` is not called: the server says
        // when this step is behind them, and until it does the screen holds.
        setPay(false);
        setFinishing("writing");
        void queryClient.invalidateQueries({ queryKey: qk.plan() });
        void queryClient.invalidateQueries({ queryKey: qk.onboarding() });
      },
    });
  }

  return (
    <OnboardShell>
      <StepDots step={3} knocker={heldHandle !== null} />
      <OnboardFace mood={trial?.premium ? "pleased" : "doubting"} />

      {/* The knock is already written. Saying so is the whole reason this screen
          lands, so it goes above the plan talk rather than under it. */}
      {firstName && <p className="nob-poster-held">{heldWaiting(firstName)}</p>}

      <h1 className="nob-poster-title">
        {trial ? (trial.premium ? PERK_PREMIUM_TITLE : PERK_REGULAR_TITLE) : PERK_CHECK_TITLE}
      </h1>

      <p className="nob-poster-lead">
        {firstName ? heldLead(firstName) : PERK_LEAD}
        {/* The offer gets its own line and a little air, so it does not read as
            a continuation of the pricing explanation. */}
        {trial && <span className="mt-2.5 block">{trialSentence(trial)}</span>}
      </p>

      {finishing === "writing" ? (
        /* THE CARD IS IN AND THE PLAN IS NOT WRITTEN DOWN YET. One quiet line,
           and no way from here to a second checkout: the two controls below are
           what would start one, and a screen that offers them over a purchase
           in flight is offering to charge somebody twice. A `status`, not an
           `alert` — nothing has gone wrong. */
        <p className="nob-poster-footnote" role="status" aria-live="polite">
          {PERK_FINISHING}
        </p>
      ) : trial && option ? (
        <>
          {finishing === "slow" ? (
            /* The window is out and the poster is a poster again — because a
               screen with nothing on it but a sentence, held for as long as a
               webhook feels like taking, is worse than the honest version: here
               is what is happening, and here are your controls back. */
            <p className="nob-poster-footnote" role="status" aria-live="polite">
              {PERK_FINISHING_SLOW}
            </p>
          ) : (
            /* The trial's limit, without a number (FR-5.8 #3, ruling C2). */
            <p className="nob-poster-footnote">{trialLimitLine(TIER_NAMES[trial.tier])}</p>
          )}
          <div className="nob-poster-cta">
            <button
              type="button"
              disabled={busy}
              onClick={() => setPay(true)}
              className="nob-poster-btn"
            >
              {startTrialLabel(trial)}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void skip()}
              className="nob-poster-ghost"
            >
              {SKIP_LABEL}
            </button>
            <p className="mt-1 text-center text-xs font-semibold text-text-soft/70" data-skip-note>
              {SKIP_NOTE}
            </p>
          </div>
        </>
      ) : (
        <>
          <p className="nob-poster-footnote">{NO_TRIAL_LEAD}</p>
          <div className="nob-poster-cta">
            <button
              type="button"
              disabled={busy}
              onClick={() => void skip()}
              className="nob-poster-btn"
            >
              {CONTINUE_FREE_LABEL}
            </button>
          </div>
        </>
      )}

      {/* ON THE PAGE ONLY WHILE NO SHEET IS OPEN (wave 103). A sheet's own
          failure is said inside the sheet, under the button that failed; here,
          behind the scrim, it was read by nobody until the sheet was closed. */}
      {error && !pay && (
        <p className="nob-poster-error" role="alert">
          {error}
        </p>
      )}

      {/* The wrong-account escape. Sign-in is sticky, so somebody with two X
          accounts can arrive as the wrong one and this is the first screen where
          they would notice. */}
      <WrongAccount me={me} className="nob-poster-legal" />

      {pay && trial && option && (
        <OnboardSheet
          title={`${TIER_NAMES[trial.tier]} · ${trial.days} day trial`}
          onClose={closeSheet}
        >
          {/* The interval choice, only where the rung is sold both ways. */}
          {trial.options.length > 1 && (
            <div className="inline-flex rounded-lg border border-border-strong p-0.5">
              {trial.options.map((o) => (
                <button
                  key={o.interval}
                  type="button"
                  aria-pressed={option.interval === o.interval}
                  onClick={() => setInterval(o.interval)}
                  className={
                    "rounded-md px-3 py-1.5 text-xs transition-colors duration-75 " +
                    (option.interval === o.interval
                      ? "bg-accent text-accent-fg"
                      : "text-muted hover:text-text")
                  }
                >
                  {o.interval === "monthly" ? "Monthly" : "Annual"}
                </button>
              ))}
            </div>
          )}
          {/* Pay $0 now AND WHAT IT COSTS AFTERWARDS, said first and plainly
              (FR-5.8 #2; owner, 2026-09-07) — then the server's own disclosure
              sentence, whole, beside the control that agrees to it. The price
              in this line is the server's own string; the client never composes
              this text and hashes exactly what is rendered here. */}
          <p className="mt-3 text-sm text-text-soft">
            {trialPriceLine(option.after_trial_monthly_display)}
          </p>
          <p className="mt-2 text-xs leading-relaxed text-faint">{option.disclosure}</p>
          {/* THE DRAWDOWN DISCLOSURE (Terms 2.11 §5.3, §8.7), beside the
              trial's own: how the allowance is drawn down, said before the
              trial starts. The server's sentence, verbatim, hashed as rendered. */}
          {view?.drawdown && (
            <p className="mt-2 text-xs leading-relaxed text-faint" data-testid="checkout-drawdown">
              {view.drawdown}
            </p>
          )}

          <label className="mt-4 flex items-start gap-3 text-sm leading-relaxed text-text-soft">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-border-strong bg-surface accent-accent
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70"
            />
            {/* The TRIAL's consent, because this step starts a trial (ToU 2.1
                §8.2, ruling R-0914-1 (2)). It consents to the charge and to
                nothing else; the terms are accepted by use (§1.2) and are a
                link beside the control rather than a clause inside the tick. */}
            <span>{TRIAL_CONSENT}</span>
          </label>
          <p className="mt-2 pl-7 text-xs leading-relaxed text-faint">
            The{" "}
            <a
              href={`${API_URL}/terms`}
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-4 transition-colors hover:text-text-soft"
            >
              terms
            </a>{" "}
            and{" "}
            <a
              href={`${API_URL}/privacy`}
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-4 transition-colors hover:text-text-soft"
            >
              privacy policy
            </a>{" "}
            apply.
          </p>
          {/* THE WITHDRAWAL-RIGHT CONTROL, WHEN THE SERVER SAYS ONE APPLIES
              (ToU 2.1 §8.6, ruling R-0914-1 (3)) — a second, distinct control
              for a consumer in the UK or the EEA, because a trial starts AI
              work at once, inside the fourteen days, and converts into the
              same subscription a paid start creates. Below the terms line, as
              on the plans view. The sentence is the server's, rendered verbatim
              and hashed as rendered; everybody else sees nothing here. */}
          {waiver && (
            <label
              className="mt-3 flex items-start gap-3 text-sm leading-relaxed text-text-soft"
              data-withdrawal-waiver
            >
              <input
                type="checkbox"
                checked={waived}
                onChange={(e) => setWaived(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-border-strong bg-surface accent-accent
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70"
              />
              <span>{waiver}</span>
            </label>
          )}

          <div className="nob-poster-cta mt-5">
            <button
              type="button"
              disabled={busy || !accepted || !view?.drawdown || (waiver !== null && !waived)}
              onClick={() => void startTrial()}
              className="nob-poster-btn"
            >
              {startTrialLabel(trial)}
            </button>
            {/* Under the button that failed, inside the sheet (wave 103). */}
            {error && (
              <p className="nob-poster-error nob-poster-sheet-error" role="alert">
                {error}
              </p>
            )}
            <button type="button" onClick={closeSheet} className="nob-poster-ghost">
              Not now
            </button>
          </div>
        </OnboardSheet>
      )}
    </OnboardShell>
  );
}
